/**
 * ESG SaaS Platform - Authentication Service
 *
 * Handles user authentication via Supabase Auth and syncs with the app backend.
 * Login, register, OAuth, and magic links are handled by Supabase.
 * The /auth/sync endpoint bridges Supabase identity with app user data.
 */

import { supabase } from './supabaseClient';
import { api, ApiError } from './api';
import { User, UserRole } from '../types';

// Request/Response types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
}

export interface UserResponse {
  id: string;
  email: string;
  name: string;
  organization_id: string;
  organization_name: string | null;
  role: UserRole;
  status: string;
  created_at: string;
  last_login: string | null;
}

export interface SyncResponse {
  user: UserResponse;
  is_new: boolean;
  needs_setup: boolean;
}

export interface UserUpdateData {
  name?: string;
  role?: UserRole;
  status?: string;
}

export interface PermissionsResponse {
  user_id: string;
  role: string;
  permissions: string[];
}

// Helper to normalize role from backend (lowercase) to frontend format (title case)
export function normalizeRole(role: string): UserRole {
  const roleMap: Record<string, UserRole> = {
    'admin': 'Admin',
    'esg_manager': 'Manager',
    'facility_manager': 'Manager',
    'manager': 'Manager',
    'analyst': 'Analyst',
    'contributor': 'Contributor',
    'viewer': 'Viewer',
    'auditor': 'Viewer',
  };
  return roleMap[role.toLowerCase()] || 'Viewer';
}

// Helper to convert backend response to frontend User type
function mapUserResponse(response: UserResponse): User {
  return {
    id: response.id,
    email: response.email,
    name: response.name,
    role: normalizeRole(response.role as string),
    companyId: response.organization_id,
    organizationName: response.organization_name || undefined,
  };
}

export const authService = {
  /**
   * Login with email and password via Supabase, then sync with backend.
   */
  async login(credentials: LoginCredentials): Promise<{ user: User; needsSetup: boolean }> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    if (error) {
      throw new ApiError(error.message, 401, { detail: error.message });
    }

    // Sync with backend
    const syncResponse = await api.post<SyncResponse>('/auth/sync');

    return {
      user: mapUserResponse(syncResponse.user),
      needsSetup: syncResponse.needs_setup,
    };
  },

  /**
   * Register a new user via Supabase, then sync with backend.
   */
  async register(data: RegisterData): Promise<{ user: User; needsSetup: boolean }> {
    const { data: authData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          name: data.name,
        },
      },
    });

    if (error) {
      throw new ApiError(error.message, 400, { detail: error.message });
    }

    // If email confirmation is required and user isn't confirmed yet
    if (authData.user && !authData.session) {
      throw new ApiError(
        'Please check your email to confirm your account.',
        200,
        { detail: 'Email confirmation required', needs_confirmation: true }
      );
    }

    // Sync with backend
    const syncResponse = await api.post<SyncResponse>('/auth/sync');

    return {
      user: mapUserResponse(syncResponse.user),
      needsSetup: syncResponse.needs_setup,
    };
  },

  /**
   * Sign in with Google OAuth via Supabase.
   * Redirects to Google, then back to /auth/callback.
   */
  async loginWithGoogle(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      throw new ApiError(error.message, 400, { detail: error.message });
    }
  },

  /**
   * Send a magic link to the user's email.
   */
  async sendMagicLink(email: string): Promise<void> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      throw new ApiError(error.message, 400, { detail: error.message });
    }
  },

  /**
   * Logout - sign out from Supabase (no backend call needed).
   */
  async logout(): Promise<void> {
    await supabase.auth.signOut();
  },

  /**
   * Get current authenticated user from backend.
   */
  async getCurrentUser(): Promise<User | null> {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      return null;
    }

    try {
      const response = await api.get<UserResponse>('/auth/me');
      return mapUserResponse(response);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Sync current Supabase session with backend.
   */
  async syncUser(): Promise<SyncResponse> {
    return api.post<SyncResponse>('/auth/sync');
  },

  /**
   * Request password reset email via Supabase.
   */
  async resetPassword(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) {
      throw new ApiError(error.message, 400, { detail: error.message });
    }
  },

  /**
   * Update password for authenticated user via Supabase.
   */
  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      throw new ApiError(error.message, 400, { detail: error.message });
    }
  },

  /**
   * Update current user's profile on the backend.
   */
  async updateProfile(data: UserUpdateData): Promise<User> {
    const response = await api.put<UserResponse>('/auth/me', data);
    return mapUserResponse(response);
  },

  /**
   * Get current user's permissions.
   */
  async getPermissions(): Promise<PermissionsResponse> {
    return api.get<PermissionsResponse>('/auth/permissions');
  },

  /**
   * Check if user is authenticated (synchronous).
   */
  isAuthenticated(): boolean {
    const storageKey = `sb-${(process.env.REACT_APP_SUPABASE_URL || '').replace(/https?:\/\//, '').split('.')[0]}-auth-token`;
    return !!localStorage.getItem(storageKey);
  },

  // ============================================================================
  // Admin-only user management functions
  // ============================================================================

  async listUsers(limit = 50, offset = 0): Promise<{ users: User[]; total: number }> {
    const response = await api.get<{ users: UserResponse[]; total: number }>(
      `/auth/users?limit=${limit}&offset=${offset}`
    );

    return {
      users: response.users.map(mapUserResponse),
      total: response.total,
    };
  },

  async inviteUser(data: { email: string; role?: string; name?: string }): Promise<any> {
    return api.post('/auth/users/invite', data);
  },

  async createUser(data: RegisterData): Promise<User> {
    // With Supabase auth, "create user" becomes "invite user"
    const response = await api.post<{ user: UserResponse }>('/auth/users/invite', {
      email: data.email,
      name: data.name,
      role: 'viewer',
    });
    return mapUserResponse(response.user);
  },

  async updateUser(userId: string, data: UserUpdateData): Promise<User> {
    const response = await api.put<UserResponse>(`/auth/users/${userId}`, data);
    return mapUserResponse(response);
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/auth/users/${userId}`);
  },
};

export default authService;
